/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: UserProfilePage.tsx
 🗓️ Created: 2025-01-06
 🧩 Description:
    User Profile page component for managing user preferences
    and personal settings following the system's standard format.

 🔧 Notes:
    - Follows the same format as UserFormView
    - Uses 4-column grid layout
    - Integrates with system components
    - Manages user-specific settings only

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../store/auth';
import { useDebugMode } from '../../hooks/useDebugMode';
import { useNotifications } from '../../hooks/useNotifications';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { User } from 'lucide-react';
import {
  useUserProfileData,
  useUserProfileForm,
  useUserProfileSubmit,
  useAvatarUpload
} from './hooks';
import { UserProfileFormFields, ChangePasswordBox } from './components';

const UserProfilePage: React.FC = () => {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { notifications, removeNotification } = useNotifications();
  const { hasPermission: debugModePermission, initializeFromUser } = useDebugMode();
  
  // Enable fixed action bar
  useFixedActionBar(true);

  // Load user profile data
  const { formData, setFormData, loading, avatar, setAvatar } = useUserProfileData();

  // Manage form state and validation
  const { errors, handleInputChange, validateForm } = useUserProfileForm(formData, setFormData);

  // Handle avatar upload
  const { uploading, handleAvatarSelect, handleAvatarRemove } = useAvatarUpload(
    user?.avatar || null,
    setAvatar
  );

  // Handle form submission
  const { handleSave, saving } = useUserProfileSubmit(
    formData,
    avatar,
    debugModePermission
  );

  // Initialize debug mode from user data
  useEffect(() => {
    if (user) {
      initializeFromUser(user);
    }
  }, [user, initializeFromUser]);

  const handleBack = () => {
    window.history.back();
  };

  const onSave = () => {
    if (!validateForm()) {
      return;
    }
    handleSave();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-20">
      {/* Header */}
      <ModuleFormHeader
        icon={<User className="h-5 w-5 text-primary" />}
        title={t('profile.editProfile')}
        backButtonText={t('common.back')}
        onBack={handleBack}
      />

      {/* Form */}
      <div className="mt-8">
        <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="space-y-4">
          <UserProfileFormFields
            formData={formData}
            errors={errors}
            avatar={avatar}
            uploading={uploading}
            debugModePermission={debugModePermission}
            handleInputChange={handleInputChange}
            handleAvatarSelect={handleAvatarSelect}
            handleAvatarRemove={handleAvatarRemove}
          />

          {/* Change Password Section */}
          <ChangePasswordBox />
        </form>
      </div>

      {/* Notification Container */}{/* Fixed Action Bar */}
      <FixedActionBar
        onSave={onSave}
        onCancel={handleBack}
        saveText={saving ? t('common.loading') : t('common.save')}
        cancelText={t('common.cancel')}
        saveDisabled={saving}
        isLoading={saving}
      />
    </div>
  );
};

export default UserProfilePage;
